import { useEffect, useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StepDetails } from './step-details';
import { StepSchedule } from './step-schedule';
import { ProgressStepper } from './progress-stepper';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/features/auth/auth-context';
import { upsertSupabaseDraft } from '@/lib/supabaseDrafts';

export type UploadFormData = {
  id: string;
  platform: string | null;
  title: string;
  description: string;
  audio: File | null;
  image: File | null;
  video: File | null;
  customThumbnail: File | string | null;
  publishTiming: 'now' | 'schedule';
  scheduledAt: string;
  visibility: 'public' | 'private' | 'unlisted';
  publishOption: 'draft' | 'publish';
};

const initialFormData: UploadFormData = {
  id: '',
  platform: null,
  title: '',
  description: '',
  audio: null,
  image: null,
  video: null,
  customThumbnail: null,
  publishTiming: 'now',
  scheduledAt: '',
  visibility: 'public',
  publishOption: 'draft',
};

type UploadModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDraftSaved?: () => void;
  draftToEdit?: Partial<UploadFormData>;
};

const generateVideoThumbnail = (videoFile: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Thumbnail generation timed out'));
    }, 10000);

    const cleanup = () => {
      clearTimeout(timeout);
      if (video.src) {
        URL.revokeObjectURL(video.src);
      }
      video.remove();
    };

    video.onloadedmetadata = () => {
      try {
        const maxWidth = 640;
        const maxHeight = 360;
        let { videoWidth, videoHeight } = video;
        
        if (videoWidth > maxWidth || videoHeight > maxHeight) {
          const ratio = Math.min(maxWidth / videoWidth, maxHeight / videoHeight);
          videoWidth = Math.floor(videoWidth * ratio);
          videoHeight = Math.floor(videoHeight * ratio);
        }
        
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    video.onseeked = () => {
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        cleanup();
        resolve(dataUrl);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    video.onerror = (e) => {
      cleanup();
      reject(new Error('Failed to load video for thumbnail generation'));
    };

    video.onabort = () => {
      cleanup();
      reject(new Error('Video loading was aborted'));
    };

    try {
      const videoUrl = URL.createObjectURL(videoFile);
      video.src = videoUrl;
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';
      video.load();
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
};

export function UploadModal({
  open,
  onOpenChange,
  onDraftSaved,
  draftToEdit,
}: UploadModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<UploadFormData>(initialFormData);

  const [initialFormState, setInitialFormState] = useState<UploadFormData>(initialFormData);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const pendingCloseRef = useRef(false);

  const isEditingDraft = Boolean(draftToEdit);

  const hasUnsavedProgress = useCallback(() => {
    const current = formData;
    const initial = initialFormState;
    
    return (
      current.title !== initial.title ||
      current.description !== initial.description ||
      current.audio !== initial.audio ||
      current.video !== initial.video ||
      current.customThumbnail !== initial.customThumbnail ||
      current.publishTiming !== initial.publishTiming ||
      current.scheduledAt !== initial.scheduledAt ||
      current.visibility !== initial.visibility
    );
  }, [formData, initialFormState]);

  const handleVideoThumbnailGeneration = useCallback(async (videoFile: File) => {
    if (!videoFile || !(videoFile instanceof File)) {
      console.warn('Invalid video file provided to thumbnail generation');
      return;
    }

    if (!videoFile.type.startsWith('video/')) {
      console.warn('File is not a video type:', videoFile.type);
      return;
    }

    try {
      setIsGeneratingThumbnail(true);
      const thumbnailDataUrl = await generateVideoThumbnail(videoFile);
      
      setFormData(prev => ({
        ...prev,
        customThumbnail: thumbnailDataUrl
      }));
      
      console.log('✅ Video thumbnail generated successfully');
      
    } catch (error) {
      console.error('Failed to generate video thumbnail:', error);
      
      setFormData(prev => ({
        ...prev,
        customThumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQwIiBoZWlnaHQ9IjM2MCIgdmlld0JveD0iMCAwIDY0MCAzNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI2NDAiIGhlaWdodD0iMzYwIiBmaWxsPSIjZjNmNGY2Ii8+Cjx0ZXh0IHg9IjMyMCIgeT0iMTgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOWNhM2FmIiBmb250LXNpemU9IjE4Ij5WaWRlbyBUaHVtYm5haWw8L3RleHQ+Cjwvc3ZnPgo='
      }));
    } finally {
      setIsGeneratingThumbnail(false);
    }
  }, []);

  useEffect(() => {
    if (formData.video && 
        formData.video instanceof File && 
        !formData.customThumbnail) {
      handleVideoThumbnailGeneration(formData.video);
    }
  }, [formData.video, formData.customThumbnail, handleVideoThumbnailGeneration]);

  const saveDraft = useCallback(async () => {
    if (!user || !hasUnsavedProgress()) return;
    
    if (!formData.title.trim() && !formData.description.trim() && !formData.audio && !formData.video) {
      return;
    }

    try {
      setIsSaving(true);
      
      const draft = {
        ...formData,
        user_id: user.id,
        savedAt: new Date().toISOString(),
        status: 'draft',
      };
      
      await upsertSupabaseDraft(draft, user.id);
      setInitialFormState(formData);
      
    } catch (error) {
      console.error('Save failed:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [user, formData, hasUnsavedProgress]);

  useEffect(() => {
    if (open) {
      if (draftToEdit) {
        const editData = {
          ...initialFormData,
          ...draftToEdit,
          id: draftToEdit.id || uuidv4(),
          audio: null,
          image: null,
          video: null,
        };
        setFormData(editData);
        setInitialFormState(editData);
        setStep(1);
      } else {
        const newData = { ...initialFormData, id: uuidv4() };
        setFormData(newData);
        setInitialFormState(newData);
        setStep(1);
      }
    }
  }, [open, draftToEdit]);

  const handleModalClose = (isOpen: boolean) => {
    if (isOpen) return;
    
    if (hasUnsavedProgress()) {
      pendingCloseRef.current = true;
      setShowExitConfirmation(true);
    } else {
      onOpenChange(false);
      window.dispatchEvent(new Event('refresh-uploads'));
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    pendingCloseRef.current = false;
    onOpenChange(false);
    window.dispatchEvent(new Event('refresh-uploads'));
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    pendingCloseRef.current = false;
  };

  const handleSaveAndExit = async () => {
    try {
      await saveDraft();
      setShowExitConfirmation(false);
      pendingCloseRef.current = false;
      onDraftSaved?.();
      onOpenChange(false);
      window.dispatchEvent(new Event('refresh-uploads'));
    } catch (error) {
      console.error('Failed to save draft and exit:', error);
    }
  };

  const handleSaveDraft = async () => {
    if (!user) return;
    
    try {
      await saveDraft();
      onDraftSaved?.();
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  // Get modal height based on step
  let modalHeight = 'h-[95vh] max-h-[800px]';
  if (step === 1) modalHeight = 'h-[90vh] max-h-[900px] min-h-[600px]';
  if (step === 2) modalHeight = 'h-[85vh] max-h-[850px] min-h-[550px]';

  // Content always uses overflow
  const contentStyle = 'flex-1 overflow-y-auto px-1 -mx-1';

  return (
    <>
      <Dialog open={open} onOpenChange={handleModalClose}>
        <DialogContent
          className={`w-[95vw] max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl flex flex-col overflow-hidden ${modalHeight}`}
          aria-describedby="upload-flow-description"
        >
          <DialogTitle className="sr-only">Upload Your Beat</DialogTitle>

          <div className="flex flex-col h-full min-h-0">
            <div className="flex-shrink-0 pb-4">
              <ProgressStepper currentStep={step} />
            </div>
            
            <div className={contentStyle}>
              {step === 1 && (
                <StepDetails
                  data={formData}
                  setData={setFormData}
                  onNext={() => setStep(2)}
                  onPrevious={undefined}
                  isEditingDraft={isEditingDraft}
                />
              )}
              {step === 2 && (
                <StepSchedule
                  data={formData}
                  setData={setFormData}
                  onPrevious={() => setStep(1)}
                  onSaveDraft={handleSaveDraft}
                  onSubmit={(videoId, youtubeUrl, uploadedThumbnailUrl) => {
                    // Upload is already tracked in BackgroundUploader
                    // Just close modal and refresh
                    onOpenChange(false);
                    window.dispatchEvent(new Event('refresh-uploads'));
                  }}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showExitConfirmation} onOpenChange={setShowExitConfirmation}>
        <AlertDialogContent className="w-[95vw] max-w-md mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Upload Progress?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to your upload. Are you sure you want to discard this progress? 
              All entered information will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 flex-col sm:flex-row">
            <AlertDialogCancel onClick={handleCancelExit} className="w-full sm:w-auto">
              Keep Editing
            </AlertDialogCancel>
            <Button 
              variant="outline" 
              onClick={handleSaveAndExit}
              disabled={isSaving || isGeneratingThumbnail}
              className="w-full sm:w-auto"
            >
              {isSaving ? 'Saving...' : 'Save & Exit'}
            </Button>
            <AlertDialogAction 
              onClick={handleConfirmExit}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}